import React, { useState, useEffect } from "react";
import { getEventDashboard, getCampaignNames } from "../api/Calling";
import "./Home.css";
import { BiTrendingUp } from "react-icons/bi";
import { FaFilter, FaSync } from "react-icons/fa";
import { MdRefresh } from "react-icons/md";

const Home = () => {
  // Temporary filter states (not applied yet)
  const [tempFilters, setTempFilters] = useState({
    dateRangeType: "today",
    customStartDate: "",
    customEndDate: "",
    campaignFilter: "",
    sourceFilter: "",
  });

  // Applied filter states (used for API calls)
  const [appliedFilters, setAppliedFilters] = useState({
    dateRangeType: "today",
    customStartDate: "",
    customEndDate: "",
    campaignFilter: "",
    sourceFilter: "",
  });

  // Data states
  const [aggregatedData, setAggregatedData] = useState([]);
  const [summary, setSummary] = useState({
    total: 0,
    success: 0,
    fail: 0,
    organic: 0,
    pid: 0,
  });
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Fetch campaign names on mount
  useEffect(() => {
    fetchCampaigns();
  }, []);

  // Fetch data when applied filters change
  useEffect(() => {
    fetchDashboardData();
  }, [appliedFilters]);

  const fetchCampaigns = async () => {
    try {
      const response = await getCampaignNames();
      if (response.status) {
        setCampaigns(response.data || []);
      }
    } catch (err) {
      console.error("Error fetching campaigns:", err);
    }
  };

  const calculateDateRange = (filters) => {
    const today = new Date();
    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    let startDate = "";
    let endDate = formatDate(today);
    console.log(today);

    switch (filters.dateRangeType) {
      case "today":
        startDate = formatDate(today);
        break;
      case "yesterday":
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        startDate = formatDate(yesterday);
        endDate = formatDate(yesterday);
        break;
      case "last15":
        const last15 = new Date(today);
        last15.setDate(last15.getDate() - 15);
        startDate = formatDate(last15);
        break;
      case "last30":
        const last30 = new Date(today);
        last30.setDate(last30.getDate() - 30);
        startDate = formatDate(last30);
        break;
      case "last6months":
        const last6months = new Date(today);
        last6months.setMonth(last6months.getMonth() - 6);
        startDate = formatDate(last6months);
        break;
      case "custom":
        startDate = filters.customStartDate;
        endDate = filters.customEndDate;
        break;
      default:
        startDate = formatDate(today);
    }

    return { startDate, endDate };
  };

  const buildQueryParams = (filters) => {
    const params = new URLSearchParams();
    const { startDate, endDate } = calculateDateRange(filters);

    // Add date range
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);

    // Add campaign filter - backend uses 'camp_name'
    if (filters.campaignFilter) {
      params.append("camp_name", filters.campaignFilter);
    }

    // Add source filter
    if (filters.sourceFilter) {
      params.append("source", filters.sourceFilter);
    }

    // Always filter for success status
    params.append("status", "success");

    return `?${params.toString()}`;
  };

  const fetchDashboardData = async () => {
    setLoading(true);
    setError("");

    try {
      const queryParams = buildQueryParams(appliedFilters);
      console.log("Fetching dashboard with params:", queryParams);

      const response = await getEventDashboard(queryParams);
      console.log("Dashboard response:", response);

      if (response.success) {
        const rawData = response.data || [];

        // Aggregate by Campaign Name and PID to count total events
        const aggregated = aggregateByPID(rawData);
        setAggregatedData(aggregated);

        // Use summary from backend
        setSummary(
          response.summary || {
            total: 0,
            success: 0,
            fail: 0,
            organic: 0,
            pid: 0,
          }
        );
      } else {
        setError(response.message || "Failed to fetch data");
        setAggregatedData([]);
        setSummary({
          total: 0,
          success: 0,
          fail: 0,
          organic: 0,
          pid: 0,
        });
      }
    } catch (err) {
      setError("Network error. Please try again.");
      console.error("Dashboard fetch error:", err);
      setAggregatedData([]);
      setSummary({
        total: 0,
        success: 0,
        fail: 0,
        organic: 0,
        pid: 0,
      });
    } finally {
      setLoading(false);
    }
  };

  // Aggregate data by Campaign Name and PID - count total events per PID
  const aggregateByPID = (data) => {
    const grouped = {};

    data.forEach((event) => {
      const key = `${event.camp_name || "N/A"}_${event.p_id || "N/A"}`;

      if (!grouped[key]) {
        grouped[key] = {
          camp_name: event.camp_name || "N/A",
          p_id: event.p_id || "N/A",
          event: event.event || "N/A",
          totalCount: 0,
        };
      }

      // Count total events for this Campaign + PID combination
      grouped[key].totalCount++;
    });

    return Object.values(grouped);
  };

  const handleApplyFilters = () => {
    setAppliedFilters({ ...tempFilters });
  };

  const handleResetFilters = () => {
    const defaultFilters = {
      dateRangeType: "today",
      customStartDate: "",
      customEndDate: "",
      campaignFilter: "",
      sourceFilter: "",
    };
    setTempFilters(defaultFilters);
    setAppliedFilters(defaultFilters);
  };

  return (
    <div className="w-full md:ml-[4.4rem] px-4 md:px-8 lg:px-10 mt-20 bg-main-bg min-h-screen pb-12">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500;600&display=swap');
        
        * {
          font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif;
        }
        
        .mono {
          font-family: 'JetBrains Mono', monospace;
        }

        .glass-effect {
          background: rgba(255, 255, 255, 0.7);
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
        }
        
        .stat-card {
          background: linear-gradient(135deg, #2563EB 0%, #1d4ed8 100%);
          position: relative;
          overflow: hidden;
        }

        .stat-card-success {
          background: linear-gradient(135deg, #10B981 0%, #059669 100%);
        }

        .stat-card-organic {
          background: linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%);
        }

        .stat-card-pid {
          background: linear-gradient(135deg, #F59E0B 0%, #D97706 100%);
        }

        .hover-lift:hover {
          transform: translateY(-2px);
        }

        .animate-spin {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
      `}</style>

      {/* HEADER SECTION */}
      <div className="mb-10">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-end gap-6 mb-8">
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-main-blue rounded-xl shadow-lg">
                <BiTrendingUp className="text-2xl text-main-white" />
              </div>
              <h1 className="text-4xl lg:text-5xl font-bold text-main-font tracking-tight">
                Event Tracker Dashboard
              </h1>
            </div>
            <p className="text-main-subfont text-base lg:text-lg font-medium ml-16">
              Monitor and analyze your event tracking data
            </p>
          </div>

          <button
            onClick={fetchDashboardData}
            className="group bg-main-blue text-main-white px-8 py-4 rounded-2xl shadow-xl hover:shadow-2xl hover:bg-main-blue/90 transition-all duration-300 flex items-center gap-3 font-semibold text-lg hover-lift"
          >
            <div className="p-2 bg-main-white/20 rounded-lg group-hover:rotate-180 transition-transform duration-500">
              <MdRefresh className="text-base" />
            </div>
            Refresh Data
          </button>
        </div>

        {/* STATS CARDS - Fixed overflow issue */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="stat-card text-main-white p-6 rounded-2xl shadow-lg overflow-hidden relative">
            <div className="absolute top-0 right-0 w-32 h-32 bg-main-white/10 rounded-full -mr-8 -mt-8"></div>
            <div className="relative z-10">
              <p className="text-sm font-medium opacity-90 mb-1">
                Total Events
              </p>
              <p className="text-4xl font-bold">{summary.total || 0}</p>
            </div>
          </div>

          <div className="stat-card-success text-main-white p-6 rounded-2xl shadow-lg overflow-hidden relative">
            <div className="absolute top-0 right-0 w-32 h-32 bg-main-white/10 rounded-full -mr-8 -mt-8"></div>
            <div className="relative z-10">
              <p className="text-sm font-medium opacity-90 mb-1">Success</p>
              <p className="text-4xl font-bold">{summary.success || 0}</p>
            </div>
          </div>

          <div className="stat-card-organic text-main-white p-6 rounded-2xl shadow-lg overflow-hidden relative">
            <div className="absolute top-0 right-0 w-32 h-32 bg-main-white/10 rounded-full -mr-8 -mt-8"></div>
            <div className="relative z-10">
              <p className="text-sm font-medium opacity-90 mb-1">Organic</p>
              <p className="text-4xl font-bold">{summary.organic || 0}</p>
            </div>
          </div>

          <div className="stat-card-pid text-main-white p-6 rounded-2xl shadow-lg overflow-hidden relative">
            <div className="absolute top-0 right-0 w-32 h-32 bg-main-white/10 rounded-full -mr-8 -mt-8"></div>
            <div className="relative z-10">
              <p className="text-sm font-medium opacity-90 mb-1">PID</p>
              <p className="text-4xl font-bold">{summary.pid || 0}</p>
            </div>
          </div>
        </div>

        {/* FILTERS SECTION */}
        <div className="bg-main-white rounded-2xl shadow-lg border border-main-border p-6 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-main-blue/10 rounded-lg">
              <FaFilter className="text-main-blue text-lg" />
            </div>
            <h2 className="text-2xl font-bold text-main-font">Filters</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            {/* Date Range */}
            <div className="space-y-2">
              <label className="block text-sm font-bold text-main-font uppercase tracking-wide">
                📅 Date Range
              </label>
              <select
                value={tempFilters.dateRangeType}
                onChange={(e) =>
                  setTempFilters({
                    ...tempFilters,
                    dateRangeType: e.target.value,
                  })
                }
                className="w-full px-4 py-3 border-2 border-main-border rounded-xl text-base font-semibold
                         focus:outline-none focus:border-main-blue focus:ring-4 focus:ring-main-blue/10 
                         transition-all duration-200"
              >
                <option value="today">Today</option>
                <option value="yesterday">Yesterday</option>
                <option value="last15">Last 15 Days</option>
                <option value="last30">Last 30 Days</option>
                <option value="last6months">Last 6 Months</option>
                <option value="custom">Custom Range</option>
              </select>
            </div>

            {/* Campaign Filter */}
            <div className="space-y-2">
              <label className="block text-sm font-bold text-main-font uppercase tracking-wide">
                🎯 Campaign
              </label>
              <select
                value={tempFilters.campaignFilter}
                onChange={(e) =>
                  setTempFilters({
                    ...tempFilters,
                    campaignFilter: e.target.value,
                  })
                }
                className="w-full px-4 py-3 border-2 border-main-border rounded-xl text-base font-semibold
                         focus:outline-none focus:border-main-blue focus:ring-4 focus:ring-main-blue/10 
                         transition-all duration-200"
              >
                <option value="">All Campaigns</option>
                {campaigns.map((camp) => (
                  <option key={camp} value={camp}>
                    {camp}
                  </option>
                ))}
              </select>
            </div>

            {/* Source Filter */}
            <div className="space-y-2">
              <label className="block text-sm font-bold text-main-font uppercase tracking-wide">
                🔗 Source
              </label>
              <select
                value={tempFilters.sourceFilter}
                onChange={(e) =>
                  setTempFilters({
                    ...tempFilters,
                    sourceFilter: e.target.value,
                  })
                }
                className="w-full px-4 py-3 border-2 border-main-border rounded-xl text-base font-semibold
                         focus:outline-none focus:border-main-blue focus:ring-4 focus:ring-main-blue/10 
                         transition-all duration-200"
              >
                <option value="">All Sources</option>
                <option value="organic">Organic</option>
                <option value="pid">PID</option>
              </select>
            </div>
          </div>

          {/* Custom Date Range */}
          {tempFilters.dateRangeType === "custom" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 p-4 bg-main-bg/50 rounded-xl border-2 border-main-border">
              <div className="space-y-2">
                <label className="block text-sm font-bold text-main-font uppercase tracking-wide">
                  Start Date
                </label>
                <input
                  type="date"
                  value={tempFilters.customStartDate}
                  onChange={(e) =>
                    setTempFilters({
                      ...tempFilters,
                      customStartDate: e.target.value,
                    })
                  }
                  className="mono w-full px-4 py-3 border-2 border-main-border rounded-xl text-base
                           focus:outline-none focus:border-main-blue focus:ring-4 focus:ring-main-blue/10 
                           transition-all duration-200"
                />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-bold text-main-font uppercase tracking-wide">
                  End Date
                </label>
                <input
                  type="date"
                  value={tempFilters.customEndDate}
                  onChange={(e) =>
                    setTempFilters({
                      ...tempFilters,
                      customEndDate: e.target.value,
                    })
                  }
                  className="mono w-full px-4 py-3 border-2 border-main-border rounded-xl text-base
                           focus:outline-none focus:border-main-blue focus:ring-4 focus:ring-main-blue/10 
                           transition-all duration-200"
                />
              </div>
            </div>
          )}

          {/* Filter Actions */}
          <div className="flex flex-wrap gap-4">
            <button
              onClick={handleApplyFilters}
              className="bg-main-blue text-main-white px-8 py-3.5 rounded-2xl font-bold 
                       hover:bg-main-blue/90 shadow-lg hover:shadow-xl transition-all duration-300 
                       flex items-center gap-2 hover-lift"
            >
              <FaFilter />
              Apply Filters
            </button>
            <button
              onClick={handleResetFilters}
              className="px-8 py-3.5 text-main-subfont font-bold hover:text-main-font 
                       hover:bg-main-white rounded-2xl transition-all duration-200 border-2 border-main-border
                       flex items-center gap-2"
            >
              <FaSync />
              Reset
            </button>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-6 bg-red-50 border-2 border-red-200 rounded-2xl">
          <p className="text-red-700 font-semibold flex items-center gap-2">
            <span className="text-2xl">⚠️</span>
            {error}
          </p>
        </div>
      )}

      {/* DATA TABLE - Shows aggregated data by Campaign and PID */}
      <div className="bg-main-white rounded-3xl shadow-2xl border border-main-border overflow-hidden">
        <div className="p-6 border-b-2 border-main-border flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div className="flex items-center gap-3">
            <div className="w-1 h-8 bg-main-blue rounded-full"></div>
            <h2 className="text-2xl font-bold text-main-font">
              Event Summary by Campaign & PID
            </h2>
          </div>
          <span className="px-6 py-2 bg-main-blue/10 text-main-blue font-bold rounded-xl mono">
            {aggregatedData.length} unique combinations
          </span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-main-th border-b-2 border-main-border">
                <th className="p-5 text-left">
                  <div className="flex items-center gap-2">
                    <div className="w-1 h-6 bg-main-blue rounded-full"></div>
                    <span className="text-main-font font-bold text-sm uppercase tracking-wide">
                      Campaign Name
                    </span>
                  </div>
                </th>
                <th className="p-5 text-left">
                  <span className="text-main-font font-bold text-sm uppercase tracking-wide">
                    PID
                  </span>
                </th>
                <th className="p-5 text-center">
                  <span className="text-main-font font-bold text-sm uppercase tracking-wide">
                    Event
                  </span>
                </th>
                <th className="p-5 text-center">
                  <span className="text-main-font font-bold text-sm uppercase tracking-wide">
                    Total Count
                  </span>
                </th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="4" className="p-16">
                    <div className="flex flex-col items-center justify-center gap-4">
                      <span className="w-12 h-12 border-4 border-main-blue/30 border-t-main-blue rounded-full animate-spin"></span>
                      <p className="text-main-subfont font-medium animate-pulse">
                        Loading data...
                      </p>
                    </div>
                  </td>
                </tr>
              ) : aggregatedData.length === 0 ? (
                <tr>
                  <td colSpan="4" className="p-16">
                    <div className="flex flex-col items-center gap-4">
                      <div className="p-6 bg-main-bg rounded-full">
                        <BiTrendingUp className="text-6xl text-main-subfont/30" />
                      </div>
                      <div className="text-center space-y-2">
                        <p className="text-main-font font-semibold text-xl">
                          No events found
                        </p>
                        <p className="text-main-subfont text-base max-w-md">
                          No events found for the selected filters. Try
                          adjusting your filter criteria.
                        </p>
                      </div>
                      <button
                        onClick={handleResetFilters}
                        className="mt-4 bg-main-blue text-main-white px-8 py-3 rounded-xl shadow-lg hover:shadow-xl hover:bg-main-blue/90 transition-all duration-300 flex items-center gap-2 font-semibold"
                      >
                        <FaSync />
                        Reset Filters
                      </button>
                    </div>
                  </td>
                </tr>
              ) : (
                aggregatedData.map((row, index) => (
                  <tr
                    key={index}
                    className="border-b border-main-border/50 hover:bg-main-thHover transition-all duration-200 group"
                  >
                    <td className="p-5">
                      <span className="text-main-font font-semibold text-base">
                        {row.camp_name}
                      </span>
                    </td>
                    <td className="p-5">
                      <span className="mono text-main-font font-semibold text-base px-3 py-1.5 bg-main-bg rounded-lg">
                        {row.p_id}
                      </span>
                    </td>
                    <td className="p-5">
                      <div className="flex justify-center">
                        <span className="px-4 py-2 bg-main-blue/10 text-main-blue font-semibold text-sm rounded-xl">
                          {row.event}
                        </span>
                      </div>
                    </td>
                    <td className="p-5">
                      <div className="flex justify-center">
                        <span className="mono px-4 py-2 bg-main-blue text-main-white font-bold text-base rounded-xl shadow-md">
                          {row.totalCount}
                        </span>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Home;
